import React, { useState } from 'react';
import {
    Alert,
    Button,
    Col,
    Container,
    Form,
    Modal,
    Row,
    Table,
    Card,
    Spinner,
    InputGroup,
} from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload,
    faUpload,
    faLink,
    faArrowLeft,
    faSearch,
    faCheckCircle,
    faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { getRequest, postRequest } from '../../../../Helper/ApiClient';

export default function SkuMapping() {
    const [currentBrandId, setCurrentBrandId] = useState<number | null>(null);
    const [flavourRefData, setFlavourRefData] = useState<any>([]);
    const [flavourRefError, setFlavourRefError] = useState(false);
    const [uploadModal, setUploadModal] = useState<{ show: boolean }>({ show: false });
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const brandsRequest = getRequest('/api/onlineshop/brands-select');
    const skuMappingsRequest = getRequest('/api/user/productReferences/count');
    const createFlavourRefMutation = postRequest('/api/user/productReferences/save_reference');
    const uploadExcelMutation = postRequest('/api/user/productReferences/uploadSheet');
    const downloadExcelRequest = getRequest(
        '/api/user/productReferences/downloadSheet',
        undefined,
        { enabled: false },
        { responseType: 'blob' }
    );

    const totalMappings = skuMappingsRequest.data?.count ?? 0;

    const fetchFlavoursRefData = async (brand_id: number | string) => {
        const res = await fetch('/api/customer_flavour_references?brand_id=' + brand_id);
        setFlavourRefError(!res.ok);
        if (!res.ok) {
            return { flavours: [] };
        }
        return res.json();
    };

    const brandSelectOnChangeHandler = (ev: React.ChangeEvent<HTMLSelectElement>) => {
        const brandId = parseInt(ev.target.value);
        setCurrentBrandId(brandId);
        fetchFlavoursRefData(brandId).then((data) => {
            setFlavourRefData(data.flavours || []);
        });
    };

    const flavourRefInputOnBlurHandler = (ev: React.FocusEvent<HTMLInputElement>) => {
        // defaultValue is the reference as loaded; leaving the field untouched must not write anything
        if (ev.currentTarget.value === ev.currentTarget.defaultValue) {
            return;
        }
        const fid = ev.target.dataset.product_attribute_id;
        const formData = new FormData();
        if (fid) {
            formData.append('product_attribute_id', fid.toString());
        }
        formData.append('cpc_reference', ev.currentTarget.value);
        createFlavourRefMutation.mutate(formData, {
            onSuccess: () => {
                if (currentBrandId) {
                    fetchFlavoursRefData(currentBrandId).then((data) => {
                        setFlavourRefData(data.flavours || []);
                    });
                }
                skuMappingsRequest.refetch();
            },
        });
    };

    const handleDownload = () => {
        setIsDownloading(true);
        downloadExcelRequest.refetch().then(({ isSuccess, data, error }) => {
            setIsDownloading(false);
            if (isSuccess && data) {
                const downloadUrl = URL.createObjectURL(new Blob([data]));
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', 'sku-mappings.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else if (error) {
                alert('Download failed: ' + (error as Error).message);
            }
        });
    };

    const handleFileOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const files = Array.from(event.target.files);
            setUploadedFile(files[0]);
        }
    };

    const handleUpload = () => {
        if (uploadedFile) {
            const formData = new FormData();
            formData.append('filepond', uploadedFile);
            uploadExcelMutation.mutate(formData, {
                onSuccess: () => {
                    if (currentBrandId) {
                        fetchFlavoursRefData(currentBrandId).then((data) => {
                            setFlavourRefData(data.flavours || []);
                        });
                    }
                    skuMappingsRequest.refetch();
                    setUploadModal({ show: false });
                    setUploadedFile(null);
                },
            });
        }
    };

    // Filter data based on search term
    const filteredData = flavourRefData.filter((item: any) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            item.name?.toLowerCase().includes(search) ||
            item.product_size?.name?.toLowerCase().includes(search) ||
            item.product_size?.brand?.name?.toLowerCase().includes(search) ||
            item.user_reference?.reference?.toLowerCase().includes(search)
        );
    });

    return (
        <Container className="py-4">
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <NavLink
                        to="/service-center/api"
                        className="text-decoration-none text-muted d-inline-flex align-items-center mb-3 text-14"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back to API Overview
                    </NavLink>
                    <h1 className="mb-2">
                        <FontAwesomeIcon icon={faLink} className="me-3 text-primary" />
                        SKU Mapping
                    </h1>
                    <p className="text-muted mb-0">
                        Connect your internal product IDs with our catalog for seamless ordering and data sync.
                    </p>
                </Col>
            </Row>

            {/* Stats & Actions */}
            <Row className="mb-4">
                <Col md={8}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-4">
                                <div>
                                    <div className="text-muted text-12">Total Mappings</div>
                                    <div className="fs-4 fw-bold">
                                        {skuMappingsRequest.isPending ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            totalMappings
                                        )}
                                    </div>
                                </div>
                                {totalMappings > 0 && (
                                    <div className="d-flex align-items-center text-success">
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                        <span className="text-14">Ready for API & Excel orders</span>
                                    </div>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleDownload}
                                    disabled={isDownloading}
                                >
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                    {isDownloading ? 'Downloading...' : 'Export'}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setUploadModal({ show: true })}
                                >
                                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                                    Import
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* How it works */}
            <Row className="mb-4">
                <Col>
                    <Card className="border-0" style={{ backgroundColor: '#f8f9fa', borderRadius: 12 }}>
                        <Card.Body className="p-4">
                            <h2 className="mb-3">
                                <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-primary" />
                                How SKU Mapping Works
                            </h2>
                            <Row>
                                <Col md={7}>
                                    <p className="mb-3 text-14">
                                        Map your internal product identifiers to our catalog. Once mapped,
                                        you can use your own SKUs for:
                                    </p>
                                    <ul className="text-14">
                                        <li>Excel order uploads in your basket</li>
                                        <li>REST API queries</li>
                                        <li>Live stock update notifications</li>
                                        <li>CSV data exports</li>
                                    </ul>
                                </Col>
                                <Col md={5}>
                                    <div className="bg-white rounded p-3">
                                        <div className="fw-semibold mb-2 text-12">Example:</div>
                                        <table className="table table-sm table-bordered mb-0 text-12">
                                            <thead style={{ backgroundColor: '#e9ecef' }}>
                                                <tr>
                                                    <th>Your SKU</th>
                                                    <th>Our Product</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td><code>WH-VAN-2K</code></td>
                                                    <td>Whey Vanilla 2kg</td>
                                                </tr>
                                                <tr>
                                                    <td><code>WH-CHO-2K</code></td>
                                                    <td>Whey Chocolate 2kg</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Brand Selection & Search */}
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Label className="fw-semibold">Select Brand</Form.Label>
                    <Form.Select onChange={brandSelectOnChangeHandler} value={currentBrandId || ''}>
                        <option value="" disabled>Choose a brand...</option>
                        {brandsRequest.isSuccess &&
                            brandsRequest.data.map((brand: any) => (
                                <option key={brand.id} value={brand.id}>
                                    {brand.name}
                                </option>
                            ))}
                    </Form.Select>
                </Col>
                {currentBrandId && flavourRefData.length > 0 && (
                    <Col md={4}>
                        <Form.Label className="fw-semibold">Search</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>
                                <FontAwesomeIcon icon={faSearch} />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Filter products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                )}
            </Row>

            {flavourRefError && (
                <Alert variant="danger" className="mb-3">
                    The references could not be loaded. The flavours are hidden so that nothing gets overwritten.
                </Alert>
            )}

            {/* Product Table */}
            <Row>
                <Col>
                    <Card className="border-0 shadow-sm">
                        <Table responsive className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th style={{ width: '15%' }}>Brand</th>
                                    <th style={{ width: '15%' }}>Category</th>
                                    <th style={{ width: '25%' }}>Product</th>
                                    <th style={{ width: '20%' }}>Attribute</th>
                                    <th style={{ width: '25%' }}>Your Reference</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!currentBrandId && (
                                    <tr>
                                        <td colSpan={5} className="text-center py-5">
                                            <div className="text-muted">
                                                <FontAwesomeIcon icon={faLink} size="2x" className="mb-3 d-block mx-auto opacity-25" />
                                                Select a brand above to view and edit product mappings
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {currentBrandId && filteredData.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="text-center py-5">
                                            <div className="text-muted">
                                                {searchTerm ? 'No products match your search' : 'No products found for this brand'}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {filteredData.map((item: any) => (
                                    <tr key={item.id}>
                                        <td className="align-middle">{item.product_size?.brand?.name}</td>
                                        <td className="align-middle">{item.product_size?.category?.name}</td>
                                        <td className="align-middle">{item.product_size?.name}</td>
                                        <td className="align-middle">{item.name}</td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter your SKU..."
                                                size="sm"
                                                onBlur={flavourRefInputOnBlurHandler}
                                                data-product_attribute_id={item.id}
                                                defaultValue={item.user_reference?.reference || ''}
                                                style={{
                                                    backgroundColor: item.user_reference?.reference ? '#e8f5e9' : undefined
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                    {currentBrandId && filteredData.length > 0 && (
                        <div className="text-muted mt-2 text-12">
                            Showing {filteredData.length} products
                            {searchTerm && ` (filtered from ${flavourRefData.length})`}
                        </div>
                    )}
                </Col>
            </Row>

            {/* Upload Modal */}
            <Modal centered show={uploadModal.show} onHide={() => setUploadModal({ show: false })}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faUpload} className="me-2" />
                        Import SKU Mappings
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-14">
                        Upload an Excel file with your SKU mappings. The file must contain a{' '}
                        <code>Naskor Flavour ID</code> column and a <code>Reference</code> column.
                        The easiest way is to use <strong>Export</strong> above, fill in the{' '}
                        <code>Reference</code> column, and re-upload that file.
                    </p>
                    <Form.Group controlId="formFile">
                        <Form.Label className="fw-semibold">Select Excel File</Form.Label>
                        <Form.Control
                            type="file"
                            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={handleFileOnChange}
                        />
                        <Form.Text className="text-muted">
                            Accepted format: .xlsx
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setUploadModal({ show: false })}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUpload}
                        disabled={uploadedFile === null || uploadExcelMutation.isPending}
                    >
                        {uploadExcelMutation.isPending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faUpload} className="me-2" />
                                Upload
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
